import { useFormikContext } from 'formik';
import { useState } from 'react';

import styles from './Password.module.less';

import { passwordStrength } from '$Utils';

/**
 * An accessible password input that optionally shows a strength meter.
 * @param {array} [descriptions] list of strings used by zxcvbn. can be a list of strings or nodes.
 * @param {string|node} [hide] text content for "hide password" state
 * @param {string} [id] HTML id attribute
 * @param {string} name HTML name attribute, usually received from Formik passthru
 * @param {string|node} [show] text content for "show password" state
 * @param {boolean} [useStrengthMeter=false] whether or not to show a strength meter powered by zxcvbn.
 * @param {string} [zxcvbnErrorText] error strings for when zxcvbn fails to load
 */

const Password = props => {
    const { setFieldValue } = useFormikContext('password_score');
    const [isShowing, setIsShowing] = useState(false);
    const [zxcvbnLoaded, setZxcvbnLoaded] = useState(null);
    const [score, setScore] = useState(0);

    const {
        descriptions = ['Very weak', 'Weak', 'Okay', 'Strong', 'Very strong'],
        hide = 'Hide',
        id = 'hvui-password',
        name,
        helpers: { setTouched, setError },
        onBlur: formikOnBlur,
        onChange: formikOnChange,
        show = 'Show',
        useStrengthMeter = false,
        zxcvbnErrorText = "We can't check the strength of your password right now. Refresh the page to try again."
    } = props;

    const checkScore = async e => {
        if (!e?.target?.value || zxcvbnLoaded === false) {
            return;
        }

        const value = e.target.value;
        const strength = await passwordStrength(value);
        if (strength == null) {
            setError(zxcvbnErrorText);
            setZxcvbnLoaded(false);
            return;
        }
        setFieldValue('password_score', strength);
        setScore(strength);
        setZxcvbnLoaded(true);
        return;
    };

    const meterLength = `${score === 0 ? 10 : score * 25}%`;
    const meterSuffix = score > 2 ? 'strong' : score > 1 ? 'okay' : 'weak';
    const meterClassName = `meter-${meterSuffix}`;

    return (
        <div className={styles['hvui-password']}>
            <div className={styles['input-container']}>
                {/* label and error are created via <FormControl /> */}
                <input
                    aria-describedby={`${id}-error ${id}-level`}
                    id={id}
                    name={name}
                    onBlur={formikOnBlur}
                    onChange={e => {
                        formikOnChange(e);
                        // Show errors as soon as they start typing
                        setTouched(true);
                        if (useStrengthMeter) {
                            checkScore(e);
                        }
                    }}
                    disabled={zxcvbnLoaded === false}
                    aria-disabled={zxcvbnLoaded === false}
                    type={isShowing ? 'text' : 'password'}
                />
                {useStrengthMeter && (
                    <input type="hidden" name="password_score" value={score} />
                )}
                <button
                    type="button"
                    role="switch"
                    aria-checked={isShowing}
                    onClick={() => setIsShowing(!isShowing)}
                >
                    {isShowing ? hide : show}
                </button>
            </div>
            {useStrengthMeter && zxcvbnLoaded !== false && (
                <div className={styles['meter-container']}>
                    <span id={`${id}-level`} className={styles['meter-text']}>
                        {descriptions[score]}
                    </span>
                    <div className={styles['meter']}>
                        <span
                            className={styles[meterClassName]}
                            style={{ width: meterLength }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Password;
